import * as ModelEvent from "@node-elion/syncron";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import BaseOptions from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import {
	NonEditableProperties,
	NonEditablePropertyNames,
} from "../../types/NonEditableProperties";
import { Base } from "..";

const logger = createLogger({ name: "ClientGroup" });

class ClientGroup extends Base {
	public static fromResponse(data: any): ClientGroup.Model {
		return {
			id: data.id,

			name: data.name,
			clientIds: data.customers?.map(({ id }) => id) ?? [],
			notes: data.notes,

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
		};
	}

	public static toRequest(model: ClientGroup.New | ClientGroup.Modified) {
		const toRequest: Record<string, any> = {};

		const { name } = model;
		if (name) toRequest.name = name;

		const { clientIds } = model;
		if (clientIds) toRequest.customerIds = clientIds;

		const { notes } = model;
		if (notes) toRequest.notes = notes;

		return toRequest;
	}

	public static async store(object: ClientGroup.New) {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.customerGroup.create(this.toRequest(object)),
			);
			return !!res;
		} catch (err) {
			return false;
		}
	}

	public static async update(object: ClientGroup.Modified) {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.customerGroup.update(
					object.id,
					this.toRequest(object),
				),
			);
			return !!res;
		} catch (err) {
			logger.error("Error in client group update method:", err);
			return false;
		}
	}

	public static async delete(id: number[] | number) {
		this.request((prpc) => prpc.theirsModel.customerGroup.delete(id));
	}

	public static async subscribe(
		options: ClientGroup.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<ClientGroup.Model>,
	): Promise<Subscription<ClientGroup.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[ClientGroup] subscribe", state);
				onUpdate({
					...state,
					models: state.models.map(this.fromResponse),
				});
			},
		});
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.customerGroup.subscribe({
					params: options,
					ping: () => true,
					onEvent: async (events) => {
						await modelEventConstructor.onEvent(events);
					},
				}),
			{ name: "ClientGroup.subscribe", metadata: options },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: ClientGroup.SubscribeOptions) =>
				subscription.update(options),
		};
	}
}

declare namespace ClientGroup {
	interface Model extends NonEditableProperties {
		name: string;
		clientIds: number[];
		notes: string;
	}

	type New = Omit<Model, NonEditablePropertyNames>;
	type Modified = Partial<New> & { readonly id: number };

	interface SubscribeOptions extends BaseOptions<ClientGroup.Model> {}
}

export default ClientGroup;
